import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useTheme } from "../context/ThemeContext";
import logviComplete from "../assets/icons/logvi-complete.png";

/** Resolver de avatar */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

function hashIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 6;
}

function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];
    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1;
  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];

  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  return undefined;
}

function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : "";
  return (first + second).toUpperCase();
}

function getDisplayName(fullName?: string | null) {
  const clean = String(fullName || "").trim();
  if (!clean) return "Usuário";

  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  return parts[0];
}

const HeaderHome: React.FC = () => {
  const navigate = useNavigate();
  const { sessao, logout } = useAuth();
  const { systemTitle } = useTheme();

  const menuRef = useRef<HTMLDivElement | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [userAreaHovered, setUserAreaHovered] = useState(false);

  const nome = sessao?.nome || "";
  const empresaRazaoSocial = sessao?.empresa_razao_social || "ERRO INTERNO";
  const empresaCnpj = sessao?.empresa_cnpj || "";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const displayName = useMemo(() => getDisplayName(nome), [nome]);

  const profileUrl = useMemo(
    () => resolveProfileUrl(pictureId, nome),
    [pictureId, nome]
  );

  const hasName = !!nome;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const doLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    setMenuOpen(false);

    try {
      try {
        await fetch("https://api.logvi-havox.com/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {}

      try {
        localStorage.removeItem("access_token");
        sessionStorage.clear();
      } catch {}

      await logout();
      window.location.replace("/login");
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <motion.header
      style={styles.container}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={styles.leftBlock}
      >
        <p style={styles.systemInfo}>
          <img
            src={logviComplete}
            alt="Logvi"
            style={styles.systemLogo}
            draggable={false}
          />

          <span style={styles.separator}>|</span>

          <span style={styles.systemTitle}>{systemTitle || "Sistema"}</span>

          <span style={styles.separator}>|</span>

          <span style={styles.companyInfo}>
            {empresaRazaoSocial}
            {empresaCnpj ? ` ${empresaCnpj}` : ""}
          </span>
        </p>
      </motion.div>

      <div style={styles.userInfo} ref={menuRef}>
        <motion.div
          style={{
            ...styles.userTrigger,
            ...(userAreaHovered ? styles.userTriggerHover : {}),
            ...(menuOpen ? styles.userTriggerOpen : {}),
          }}
          onMouseEnter={() => setUserAreaHovered(true)}
          onMouseLeave={() => setUserAreaHovered(false)}
          onClick={() => setMenuOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setMenuOpen((prev) => !prev);
            }
          }}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          whileHover={{ y: -0.5 }}
          whileTap={{ scale: 0.99 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
        >
          <span
            style={{
              ...styles.name,
              color: userAreaHovered || menuOpen ? "#000000" : "#FFFFFF",
            }}
          >
            {displayName}
          </span>

          {profileUrl ? (
            <motion.img
              key="avatar-img"
              src={profileUrl}
              alt={displayName}
              style={styles.avatar}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              loading="lazy"
            />
          ) : (
            <motion.div
              key="avatar-fallback"
              style={{ ...styles.avatar, ...styles.avatarFallback }}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              {hasName ? getInitials(nome) : ""}
            </motion.div>
          )}
        </motion.div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <button
                type="button"
                style={styles.menuItem}
                onClick={doLogout}
                disabled={loggingOut}
              >
                {loggingOut ? "Desconectando..." : "Desconectar Sessão"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "72px",
    minHeight: "72px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    overflow: "visible",
    pointerEvents: "none",
  },

  leftBlock: {
    display: "flex",
    alignItems: "center",
    marginRight: 18,
    flex: 1,
    minWidth: 0,
    pointerEvents: "auto",
  },

  systemInfo: {
    margin: 0,
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    maxWidth: "100%",
    textShadow: "0 2px 10px rgba(0,0,0,0.18)",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },

  systemLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    flexShrink: 0,
    display: "block",
    marginTop: 5,
  },

  systemTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    flexShrink: 0,
  },

  companyInfo: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
  },

  separator: {
    opacity: 0.65,
    color: "#FFFFFF",
    flexShrink: 0,
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    position: "relative",
    minWidth: 0,
    flexShrink: 0,
    pointerEvents: "auto",
  },

  userTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 38,
    padding: "1px 10px",
    borderRadius: 999,
    cursor: "pointer",
    userSelect: "none",
    transition:
      "background 0.22s ease, box-shadow 0.22s ease, transform 0.18s ease, border 0.22s ease, color 0.22s ease",

    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    color: "#5f6368",
  },

  userTriggerHover: {
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(255,255,255,0.55)",
    color: "#000000",
  },

  userTriggerOpen: {
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(255,255,255,0.62)",
    boxShadow: "0 8px 22px rgba(0,0,0,0.12)",
    color: "#000000",
  },

  name: {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "inherit",
    transition: "color 0.2s ease",
  },

  avatar: {
    width: 26,
    height: 26,
    minWidth: 26,
    minHeight: 26,
    borderRadius: "50%",
    objectFit: "cover",
    userSelect: "none",
    background: "#0B5268",
    flexShrink: 0,
  },

  avatarFallback: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  menu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 220,
    borderRadius: 14,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(0,30,39,0.08)",
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
    overflow: "hidden",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
  },

  menuItem: {
    width: "100%",
    minHeight: 44,
    padding: "10px 14px",
    border: "none",
    background: "transparent",
    color: "#17313A",
    textAlign: "left",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    borderBottom: "1px solid rgba(0,30,39,0.06)",
  },
};

export default HeaderHome;
